import { useCallback, useEffect, useRef, useState } from 'react'

import { errorView } from '@krp/client-runtime'
import { withImageType } from '@krp/ui-kit'
import {
  COMMON_PHOTO_MAX_BYTES,
  UPLOAD_STAGES,
  cutoutBlob,
  newUuid,
  photoRegistryApi,
  photoRegistryTag,
  prepareStagingPhoto,
  sameBox,
  saveStagingPhoto,
  type CropBox,
  type PhotoCropReviewVm,
  type PhotoStaging,
  type PhotoUploadVm,
} from '@krp/platform-ui'

import { useAppDispatch } from '../../app/hooks'

/**
 * Чем кончилась загрузка: успех с ответом сервера или неуспех. `cancelled` — человек сам отказался (отмена на любом этапе, в том числе «Отмена»
 * на проверке обрезки): это не сбой, и в ленту «Не сохранено» он не попадает. Иначе причина уже показана через `view`.
 */
export type UploadOutcome = { ok: true; staging: PhotoStaging } | { ok: false; cancelled: boolean }

interface Attempt {
  productId: string
  file: File
  previewUrl: string | null
  abort: AbortController
  /** Завершает обещание, которое вернул `run` / `retry`. */
  settle: (outcome: UploadOutcome) => void
  /** Вырез и его описание после подготовки; живёт, пока человек проверяет обрезку и пока фото сохраняется. */
  cutout: { blob: Blob; review: PhotoCropReviewVm } | null
  /** Последняя отправленная рамка и её ключ операции: та же рамка — тот же ключ (потерянный ответ не даст второго фото), другая — новый. */
  save: { box: CropBox; operationId: string } | null
}

type Upload = Omit<PhotoUploadVm, 'stage' | 'cancelable' | 'fraction'>

/** Отозвать адрес выреза на устройстве, когда проверка кончилась. */
function release(attempt: Attempt | null): void {
  if (attempt === null || attempt.cutout === null) return
  URL.revokeObjectURL(attempt.cutout.review.cutoutUrl)
  attempt.cutout = null
}

/**
 * Загрузка одного кадра во ВРЕМЕННУЮ загрузку общего товара (RP-FB56, RP-FB73, PHI-13a; поток PSR-5): файл как выбран (браузер не перекодирует) → подготовка на сервере
 * (вырез без фона и предложенная рамка, ничего не сохраняется) → ПРОВЕРКА ОБРЕЗКИ человеком («Принять» с рамкой или «Отмена») → сохранение по принятой рамке
 * (вместе с вырезом уходит и исходный файл). Фото остаётся временной загрузкой и никуда не отправляется: в источник оно уходит кнопкой в окне фото товара. Файл и ход живут здесь
 * (File и Blob в Redux класть нельзя). Рамка сохраняется под своим ключом операции: повтор той же рамки сервер узнаёт и не создаёт второе фото,
 * другая рамка — новый ключ. Отказ сохранения возвращает человека к проверке с причиной дословно и с той же рамкой — править и принимать ещё раз.
 * Обещание `run` / `retry` завершается, когда фото сохранено или человек отказался; ожидание проверки — часть загрузки.
 */
export function usePhotoUploadTask(): {
  view: PhotoUploadVm | null
  run: (productId: string, file: File, previewUrl: string | null) => Promise<UploadOutcome>
  retry: () => Promise<UploadOutcome>
  accept: (box: CropBox) => Promise<void>
  cancel: () => void
  dismiss: () => void
} {
  const dispatch = useAppDispatch()
  const [upload, setUpload] = useState<Upload | null>(null)
  const attempt = useRef<Attempt | null>(null)

  useEffect(
    () => () => {
      attempt.current?.abort.abort()
      release(attempt.current)
    },
    [],
  )

  const prepare = useCallback(
    async (current: Attempt): Promise<void> => {
      const { file, productId, abort } = current
      const base = { fileName: file.name, previewUrl: current.previewUrl, loaded: 0, total: null, error: null } as const
      const fail = (error: NonNullable<Upload['error']>) => {
        setUpload({ ...base, phase: 'failed', error })
        current.settle({ ok: false, cancelled: false })
      }
      setUpload({ ...base, phase: 'preparing' })
      // Файл уходит как выбран; только пустой тип (HEIC в Chrome на Windows) получает тип по расширению — сервер требует image/*.
      const prepared = withImageType(file)
      if (abort.signal.aborted) {
        current.settle({ ok: false, cancelled: true })
        return
      }
      if (prepared.size > COMMON_PHOTO_MAX_BYTES) {
        fail({ kind: 'client', code: 'photo-too-large', message: 'Фото больше 30 МиБ. Выберите другое.', correlationId: null })
        return
      }
      setUpload({ ...base, phase: 'sending', total: prepared.size })
      const outcome = await dispatch(
        prepareStagingPhoto(productId, prepared, abort.signal, (loaded, total) =>
          setUpload((state) =>
            state === null || state.phase === 'failed' ? state : { ...state, phase: total !== null && loaded >= total ? 'waiting' : 'sending', loaded, total },
          ),
        ),
      )
      if (abort.signal.aborted) {
        setUpload(null)
        current.settle({ ok: false, cancelled: true })
        return
      }
      if (!outcome.ok) {
        const error = errorView(outcome.error)
        fail({ kind: error.kind, code: error.code, message: error.message, correlationId: error.correlationId })
        return
      }
      const data = outcome.data
      let blob: Blob
      try {
        blob = cutoutBlob(data.cutoutBase64, data.contentType)
      } catch {
        fail({
          kind: 'client',
          code: 'invalid-response',
          message: 'Сервер вернул фото без фона в неразборчивом виде. Повторите загрузку; если повторится — сообщите администратору.',
          correlationId: null,
        })
        return
      }
      const review: PhotoCropReviewVm = {
        cutoutUrl: URL.createObjectURL(blob),
        width: data.width,
        height: data.height,
        proposedBox: data.proposedBox,
        statusText: data.cropStatusText,
        margin: data.margin,
        maxSide: data.maxSide,
      }
      current.cutout = { blob, review }
      setUpload({ ...base, phase: 'review', review })
    },
    [dispatch],
  )

  const run = useCallback(
    (productId: string, file: File, previewUrl: string | null) =>
      new Promise<UploadOutcome>((resolve) => {
        release(attempt.current)
        const current: Attempt = { productId, file, previewUrl, abort: new AbortController(), settle: resolve, cutout: null, save: null }
        attempt.current = current
        void prepare(current)
      }),
    [prepare],
  )

  const retry = useCallback(
    () =>
      new Promise<UploadOutcome>((resolve) => {
        const current = attempt.current
        if (current === null) {
          resolve({ ok: false, cancelled: false })
          return
        }
        release(current)
        current.abort = new AbortController()
        current.settle = resolve
        current.save = null
        void prepare(current)
      }),
    [prepare],
  )

  const accept = useCallback(
    async (box: CropBox): Promise<void> => {
      const current = attempt.current
      if (current === null || current.cutout === null) return
      const { blob, review } = current.cutout
      const save = { box, operationId: current.save !== null && sameBox(current.save.box, box) ? current.save.operationId : newUuid() }
      current.save = save
      current.abort = new AbortController()
      const { signal } = current.abort
      const base = { fileName: current.file.name, previewUrl: current.previewUrl, loaded: 0, total: null, error: null, review } as const
      setUpload({ ...base, phase: 'sending', total: blob.size })
      const outcome = await dispatch(saveStagingPhoto(current.productId, blob, current.file, box, save.operationId, signal))
      if (signal.aborted) {
        setUpload(null)
        current.settle({ ok: false, cancelled: true })
        return
      }
      if (outcome.ok) {
        release(current)
        attempt.current = null
        setUpload(null)
        // Новая временная загрузка попадает в окно фото товара без перезагрузки: реестр и список загрузок перечитываются.
        dispatch(photoRegistryApi.util.invalidateTags([photoRegistryTag(current.productId)]))
        current.settle({ ok: true, staging: outcome.data })
        return
      }
      // Не сохранено: человек остаётся на проверке с той же рамкой и читает причину; «Принять» повторит (та же рамка — тот же ключ операции).
      const error = errorView(outcome.error)
      setUpload({ ...base, phase: 'review', error: { kind: error.kind, code: error.code, message: error.message, correlationId: error.correlationId } })
    },
    [dispatch],
  )

  const cancel = useCallback(() => {
    const current = attempt.current
    attempt.current = null
    if (current !== null) {
      current.abort.abort()
      release(current)
      current.settle({ ok: false, cancelled: true })
    }
    setUpload(null)
  }, [])

  const dismiss = useCallback(() => {
    release(attempt.current)
    attempt.current = null
    setUpload(null)
  }, [])

  const view: PhotoUploadVm | null =
    upload === null
      ? null
      : {
          ...upload,
          stage: UPLOAD_STAGES[upload.phase],
          cancelable: upload.phase !== 'failed',
          fraction: upload.total !== null && upload.total > 0 && upload.review === undefined ? Math.min(1, upload.loaded / upload.total) : undefined,
        }
  return { view, run, retry, accept, cancel, dismiss }
}
